import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { ContactEditFormValues, ContactPhoneRow } from "services/api/contacts/types";
import { getContactPhonesAPI, deleteContactPhoneAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { Loader } from "ui/components/Loader";
import { TableSearch } from "ui/components/Table/TableSearch";
import { CONTACT_EDIT_PHONE_COLUMNS } from "./consts";
import { Dialog } from "ui/components/Dialog";
import ContactPhoneEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactPhoneEditCard/ContactPhoneEditCard";

interface ContactPhoneStepProps {
  formValues: ContactEditFormValues;
}

const ContactPhoneStep: React.FC<ContactPhoneStepProps> = ({ formValues: parentFormValues }) => {
  const contactId = parentFormValues.id;
  const { t } = useTranslate();
  const [phones, setPhones] = useState<ContactPhoneRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editPhoneId, setEditPhoneId] = useState<string | null>(null);
  const [deletePhoneId, setDeletePhoneId] = useState<string | null>(null);

  const fetchPhones = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const result = await getContactPhonesAPI(initialDataWithPagination.pagination, contactId);
      setPhones(result.data);
    } catch {
      setPhones([]);
    }
    setIsLoading(false);
  }, [contactId]);

  useEffect(() => {
    if (contactId) fetchPhones();
  }, [contactId]);

  const handleRowClick = useCallback((phoneId: string) => {
    setEditPhoneId(phoneId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditPhoneId(null);
    setShowEdit(true);
  };

  // Klik na kantu ne smije otvoriti i uređivanje retka.
  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLDivElement>, phoneId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDeletePhoneId(phoneId);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!contactId || !deletePhoneId) return;
    await deleteContactPhoneAPI(t, contactId, deletePhoneId);
    setDeletePhoneId(null);
    fetchPhones();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditPhoneId(null);
    fetchPhones();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Telefoni {parentFormValues.contactTypeId === "C" ? "tvrtke" : "osobe"}</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte telefonske brojeve kontakta klikom na gumb <strong>"Dodaj telefon"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {phones.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj telefon"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  {/* Isti izgled kao PP aparati na unosu vozila: klik na redak uređuje, kanta briše. */}
                  <TableSearch
                    columns={CONTACT_EDIT_PHONE_COLUMNS(handleDeleteClick)}
                    data={phones}
                    selectableRows={false}
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                  />
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj telefon" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ContactPhoneEditCard
        contactId={contactId ?? ""}
        phoneId={editPhoneId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
      <Dialog
        open={!!deletePhoneId}
        title="Brisanje telefona"
        message="Jeste li sigurni da želite obrisati ovaj telefon?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletePhoneId(null)}
      />
    </>
  );
};

export default memo(ContactPhoneStep);
